import {useRef} from "react";
import {campaignRequestReserve, type PeelCandidate} from "./peelTagSlot";

/**
 * The slot's memory of what it was showing when the campaign request opened (ui#2492).
 *
 * Thin adapter: the rule for what survives the request is {@link campaignRequestReserve}, and this
 * holds the one fact a pure function cannot — which tags were in the slot BEFORE the request, which is
 * what tells an instant card mid-download from a hosted card's first render.
 *
 * <b>A ref rather than state.</b> The memory is read in the same render that produces the next list,
 * so state would answer one render late and the invite would blink out for that frame — the very
 * flicker this exists to remove. Nothing here changes what this render paints, so there is nothing to
 * re-render for either. It records only while the slot is free, so a render discarded by React can
 * leave behind no more than the ids that render would have shown.
 *
 * @param candidates what the slot is free to show, from `successGiftCandidates`
 * @param campaignLoading the campaign is being asked about, so the slot is reserved for its answer
 */
export function useCampaignRequestReserve<Node>(candidates: readonly PeelCandidate<Node>[],
	campaignLoading: boolean): PeelCandidate<Node>[] {
	const held = useRef<readonly string[]>([]);
	if (!campaignLoading) held.current = candidates.map(candidate => candidate.id);
	return campaignRequestReserve(candidates, held.current, campaignLoading);
}
